/**
 * Site identity used in titles, canonical URLs, social cards and structured data.
 *
 * Canonical URLs always point at production, so the dev server and preview
 * builds never compete with the live site in search results.
 */

export const SITE_NAME = "Deen Over Dunya";
export const SITE_URL = "https://deenoverdunya.org";
export const SITE_TAGLINE = "Authentic Islamic Knowledge, Verified & Sourced";
export const DEFAULT_DESCRIPTION =
  "Deen Over Dunya is a free Islamic education platform: the Seerah of Prophet Muhammad ﷺ, Quran with classical tafsir, " +
  "hadith with their chains of narration, and misconceptions about Islam answered from verified sources.";
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/** `/seerah/` → `https://deenoverdunya.org/seerah` (no trailing slash, query or hash). */
export function absoluteUrl(path: string): string {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, "");
  return `${SITE_URL}${clean || "/"}`;
}

/** Trims text to a search-snippet length on a word boundary. */
export function snippet(text: string | null | undefined, max = 160): string | undefined {
  if (!text) return undefined;
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${space > max * 0.6 ? cut.slice(0, space) : cut}…`;
}

/** JSON for a `<script type="application/ld+json">`, safe to inline in HTML. */
export function jsonLdHtml(data: object): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

const ORGANIZATION = { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: SITE_NAME, url: `${SITE_URL}/` };

/** schema.org Article for a single piece of content. */
export function articleJsonLd(a: { title: string; description?: string; path: string; datePublished?: string; section?: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title.length > 110 ? `${a.title.slice(0, 109)}…` : a.title,
    description: a.description,
    articleSection: a.section,
    datePublished: a.datePublished,
    image: DEFAULT_IMAGE,
    mainEntityOfPage: absoluteUrl(a.path),
    author: ORGANIZATION,
    publisher: ORGANIZATION,
  };
}
